'use client';
import React from 'react';
import clsx from 'clsx';
import { styled } from '@linaria/react';
import { LayoutGroup, motion } from 'framer-motion';

import { range } from '@/utils';
import Demo, { DemoSliderControl } from '@/components/Demo';
import Paper from '@/components/Paper';

import Equation from './Equation';
import styles from './DivisionGroupsDemo.module.css';

function DivisionGroupsDemo({
  numOfItems = 12,
  initialNumOfGroups = 1,
  includeRemainderArea,
}) {
  const id = React.useId();

  return (
    <LayoutGroup>
      <Demo
        id={id}
        initialValues={{ numOfGroups: initialNumOfGroups }}
        controlPosition="top"
        customGrid={{
          gridTemplateColumns: '1fr',
        }}
        controls={[
          <DemoSliderControl
            key="numOfGroups"
            id="numOfGroups"
            label="Number of Groups"
            min={1}
            max={4}
            wrapperStyle={{
              maxWidth: 350,
              width: '100%',
              margin: '0 auto',
            }}
          />,
        ]}
        outputWrapper={OutputWrapper}
      >
        {({ numOfGroups }) => {
          const numOfItemsPerGroup = Math.floor(
            numOfItems / numOfGroups
          );
          const totalNumInGroups = numOfGroups * numOfItemsPerGroup;

          const remainder = includeRemainderArea
            ? numOfItems % numOfGroups
            : null;

          const gridStructure =
            numOfGroups < 4
              ? {
                  gridTemplateColumns: `repeat(${numOfGroups}, 1fr)`,
                }
              : {
                  gridTemplateColumns: '1fr 1fr',
                  gridTemplateRows: '1fr 1fr',
                };

          return (
            <section className={styles.wrapper}>
              <div className={styles.demoWrapper}>
                <div
                  className={clsx(styles.demoArea)}
                  style={gridStructure}
                >
                  {range(numOfGroups).map((groupIndex) => {
                    const totalInPreviousGroups =
                      groupIndex * numOfItemsPerGroup;

                    return (
                      <div key={groupIndex} className={styles.group}>
                        {range(
                          totalInPreviousGroups,
                          totalInPreviousGroups + numOfItemsPerGroup
                        ).map((index) => {
                          const layoutId = `${id}-${index}`;

                          return (
                            <motion.div
                              key={layoutId}
                              layoutId={layoutId}
                              className={styles.item}
                            />
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>

              {includeRemainderArea && (
                <div className={styles.remainderArea}>
                  <p className={styles.remainderHeading}>
                    Remainder Area
                  </p>

                  {range(totalNumInGroups, numOfItems)
                    .reverse()
                    .map((index) => {
                      const layoutId = `${id}-${index}`;

                      return (
                        <motion.div
                          key={layoutId}
                          layoutId={layoutId}
                          className={styles.item}
                        />
                      );
                    })}
                </div>
              )}

              <Equation
                dividend={numOfItems}
                divisor={numOfGroups}
                remainder={remainder}
              />
            </section>
          );
        }}
      </Demo>
    </LayoutGroup>
  );
}

const OutputWrapper = styled(Paper)`
  position: relative;
  padding: 32px;
`;

export default DivisionGroupsDemo;
